/**
 * Fleet KPI dashboard.
 *
 * Peribott Dynamic LLP — Author: Amit Jagnani
 *
 * v6.3.38. Charts are hand-rolled inline SVG on purpose: the project has no
 * charting dependency, and adding one (recharts/chart.js) would change the
 * npm install step of a production build for a couple of simple plots.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField, MenuItem, Button,
  Chip, LinearProgress, Alert, Tabs, Tab, Tooltip,
} from '@mui/material';
import { Refresh, Timeline, BatteryFull, Wifi, Straighten } from '@mui/icons-material';
import apiClient from '../../utils/apiClient';

type Point = { date: string; value: number | null; min_value?: number };

const PERIODS = [
  { label: 'Last 24 hours', value: 1 },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

/** Minimal responsive line/bar chart. No external dependency. */
const MiniChart: React.FC<{
  points: Point[]; color?: string; unit?: string; type?: 'line' | 'bar'; height?: number;
}> = ({ points, color = '#2196f3', unit = '', type = 'line', height = 180 }) => {
  const clean = points.filter((p) => p.value != null) as { date: string; value: number }[];
  if (clean.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No data in this period</Typography>
      </Box>
    );
  }

  const W = 600, H = height, PAD = { l: 44, r: 12, t: 12, b: 26 };
  const max = Math.max(...clean.map((p) => p.value));
  const min = Math.min(0, ...clean.map((p) => p.value));
  const span = max - min || 1;
  const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
  const x = (i: number) => PAD.l + (clean.length === 1 ? iw / 2 : (i / (clean.length - 1)) * iw);
  const y = (v: number) => PAD.t + ih - ((v - min) / span) * ih;

  const ticks = [min, min + span / 2, max];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img">
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke="currentColor" strokeOpacity={0.12} />
            <text x={PAD.l - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="currentColor" opacity={0.6}>
              {t >= 1000 ? `${(t / 1000).toFixed(1)}k` : t.toFixed(t < 10 ? 1 : 0)}
            </text>
          </g>
        ))}

        {type === 'bar' ? (
          clean.map((p, i) => {
            const bw = Math.max(2, (iw / clean.length) * 0.65);
            return (
              <rect key={i} x={x(i) - bw / 2} y={y(p.value)} width={bw}
                height={Math.max(1, y(min) - y(p.value))} fill={color} opacity={0.85}>
                <title>{`${p.date}: ${p.value.toFixed(1)}${unit}`}</title>
              </rect>
            );
          })
        ) : (
          <>
            <polyline
              points={clean.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')}
              fill="none" stroke={color} strokeWidth={2}
              strokeLinejoin="round" strokeLinecap="round"
            />
            {clean.map((p, i) => (
              <circle key={i} cx={x(i)} cy={y(p.value)} r={clean.length > 40 ? 0 : 3} fill={color}>
                <title>{`${p.date}: ${p.value.toFixed(1)}${unit}`}</title>
              </circle>
            ))}
          </>
        )}

        {clean.map((p, i) =>
          (i === 0 || i === clean.length - 1 || (clean.length > 4 && i === Math.floor(clean.length / 2))) ? (
            <text key={`x${i}`} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10"
              fill="currentColor" opacity={0.6}>{p.date.slice(5)}</text>
          ) : null
        )}
      </svg>
    </Box>
  );
};

const KpiCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> =
  ({ label, value, sub, color, icon }) => (
    <Card sx={{ height: '100%', background: `${color}10`, border: `1px solid ${color}25` }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom noWrap>{label}</Typography>
            <Typography fontWeight={700} sx={{ color, fontSize: { xs: '1.6rem', md: '1.9rem' }, lineHeight: 1.2 }}>
              {value}
            </Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: `${color}20`, display: 'flex', flexShrink: 0 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

const FleetAnalytics: React.FC = () => {
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<any>(null);
  const [series, setSeries] = useState<Record<string, Point[]>>({});
  const [battery, setBattery] = useState<any>(null);
  const [conn, setConn] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, batt, c, tsBatt, tsDist] = await Promise.all([
        apiClient.get(`/analytics/fleet-summary?days=${days}`).then((r) => r.data),
        apiClient.get(`/analytics/battery-health?days=${days}`).then((r) => r.data).catch(() => null),
        apiClient.get(`/analytics/connectivity?days=${days}`).then((r) => r.data).catch(() => null),
        apiClient.get(`/analytics/timeseries?days=${days}&metric=battery`).then((r) => r.data).catch(() => null),
        apiClient.get(`/analytics/timeseries?days=${days}&metric=distance`).then((r) => r.data).catch(() => null),
      ]);
      setSummary(s);
      setBattery(batt);
      setConn(c);
      setSeries({ battery: tsBatt?.points || [], distance: tsDist?.points || [] });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const totals = summary?.totals || {};
  const robots: any[] = useMemo(() => summary?.robots || [], [summary]);
  const avgAvailability = useMemo(() => {
    const v = robots.filter((r) => r.availability_pct != null);
    return v.length ? v.reduce((a, r) => a + r.availability_pct, 0) / v.length : null;
  }, [robots]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h4" fontWeight={700}>Fleet Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            Utilisation, distance and reliability across your fleet
          </Typography>
        </Box>
        <TextField select size="small" label="Period" value={days}
          onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 160 }}>
          {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </TextField>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Distance travelled" color="#2196f3"
            value={totals.distance_km != null ? `${totals.distance_km} km` : '—'}
            sub={`${totals.reporting_count ?? 0} of ${totals.robot_count ?? 0} robots reporting`}
            icon={<Straighten sx={{ color: '#2196f3' }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Total online time" color="#69f0ae"
            value={totals.online_hours != null ? `${totals.online_hours} h` : '—'}
            sub={`across ${totals.robot_count ?? 0} robots`}
            icon={<Wifi sx={{ color: '#69f0ae' }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Avg availability" color="#ffab40"
            value={avgAvailability != null ? `${avgAvailability.toFixed(1)}%` : '—'}
            sub="connected / period"
            icon={<Timeline sx={{ color: '#ffab40' }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Telemetry samples" color="#b388ff"
            value={totals.samples != null ? Intl.NumberFormat().format(totals.samples) : '—'}
            sub={`last ${days} day${days > 1 ? 's' : ''}`}
            icon={<BatteryFull sx={{ color: '#b388ff' }} />} />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Distance / day" />
          <Tab label="Battery / day" />
        </Tabs>
        <CardContent>
          {tab === 0 && <MiniChart points={series.distance || []} color="#2196f3" unit=" m" type="bar" />}
          {tab === 1 && <MiniChart points={series.battery || []} color="#69f0ae" unit="%" type="line" />}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="h6" fontWeight={600}>Per-robot performance</Typography>
        </CardContent>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Serial</TableCell>
                <TableCell align="right">Distance (m)</TableCell>
                <TableCell align="right">Online (h)</TableCell>
                <TableCell align="right">Availability</TableCell>
                <TableCell align="right">Avg battery</TableCell>
                <TableCell align="right">Min battery</TableCell>
                <TableCell>Last seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {robots.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading…' : 'No telemetry recorded in this period.'}
                  </Typography>
                </TableCell></TableRow>
              )}
              {robots.map((r) => (
                <TableRow key={r.robot_serial} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{r.robot_serial}</Typography></TableCell>
                  <TableCell align="right">{Intl.NumberFormat().format(r.distance_m)}</TableCell>
                  <TableCell align="right">{r.online_hours}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={`${r.availability_pct}%`}
                      color={r.availability_pct >= 90 ? 'success' : r.availability_pct >= 50 ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell align="right">{r.avg_battery ?? '—'}</TableCell>
                  <TableCell align="right">
                    {r.min_battery == null ? '—' : (
                      <Typography variant="body2"
                        color={r.min_battery < 10 ? 'error.main' : r.min_battery < 20 ? 'warning.main' : 'inherit'}>
                        {r.min_battery}%
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.last_seen ? new Date(r.last_seen).toLocaleString() : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {summary?.note && (
          <CardContent sx={{ pt: 1 }}>
            <Typography variant="caption" color="text.secondary">{summary.note}</Typography>
          </CardContent>
        )}
      </Card>

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Battery health</Typography>
              {(battery?.robots || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No battery data.</Typography>
              ) : (battery.robots).map((b: any) => (
                <Box key={b.robot_serial} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>{b.robot_serial}</Typography>
                    <Tooltip title="Share of samples below 20%">
                      <Typography variant="caption"
                        color={b.low_pct > 20 ? 'warning.main' : 'text.secondary'}>
                        {b.low_pct}% low{b.critical_samples > 0 ? ` · ${b.critical_samples} critical` : ''}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <LinearProgress variant="determinate" value={Math.min(100, b.avg_battery)}
                    color={b.avg_battery < 30 ? 'warning' : 'success'}
                    sx={{ height: 6, borderRadius: 3 }} />
                  <Typography variant="caption" color="text.secondary">
                    avg {b.avg_battery}% · min {b.min_battery}%
                  </Typography>
                </Box>
              ))}
              {battery?.note && (
                <Typography variant="caption" color="text.secondary">{battery.note}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Connection stability</Typography>
              {(conn?.robots || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No sessions recorded.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Serial</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                        <TableCell align="right">Reconnects/day</TableCell>
                        <TableCell align="right">Uptime</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {conn.robots.map((c: any) => (
                        <TableRow key={c.robot_serial}>
                          <TableCell>{c.robot_serial}</TableCell>
                          <TableCell align="right">{c.sessions}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2"
                              color={c.reconnects_per_day > 10 ? 'error.main'
                                : c.reconnects_per_day > 3 ? 'warning.main' : 'inherit'}>
                              {c.reconnects_per_day}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{c.availability_pct}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {conn?.note && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {conn.note}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FleetAnalytics;
